import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users as UsersIcon,
  Search,
  Filter,
  RefreshCw,
  Plus,
  Edit,
  Lock,
  Unlock,
  Ban,
  CheckCircle,
  XCircle,
  Shield,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { userApi } from '../services/api';
import {
  formatDate,
  formatRelativeTime,
  debounce
} from '../utils/helpers';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    search: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userApi.getUsers(filters);
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const handleFilterChange = debounce((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, 300);

  const clearFilters = () => {
    setFilters({
      status: '',
      role: '',
      search: ''
    });
  };

  const handleUnlock = async (userId) => {
    if (!window.confirm('Are you sure you want to unlock this user account?')) {
      return;
    }

    try {
      await userApi.unlockUser(userId);
      await loadUsers();
    } catch (err) {
      console.error('Failed to unlock user:', err);
      alert('Failed to unlock user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleActivate = async (userId) => {
    try {
      await userApi.activateUser(userId);
      await loadUsers();
    } catch (err) {
      console.error('Failed to activate user:', err);
      alert('Failed to activate user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await userApi.deactivateUser(userId);
      await loadUsers();
    } catch (err) {
      console.error('Failed to deactivate user:', err);
      alert('Failed to deactivate user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResetPassword = async (userId, username) => {
    if (!window.confirm(`Reset password for ${username}? They will need to change it on next login.`)) {
      return;
    }

    try {
      const response = await userApi.resetPassword(userId);
      alert(`Password reset successfully!\n\nTemporary password: ${response.temporaryPassword}\n\nPlease share this securely with the user.`);
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password: ' + (err.response?.data?.error || err.message));
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      ADMIN: 'badge bg-purple-100 text-purple-800 border-purple-200',
      OPERATOR: 'badge bg-blue-100 text-blue-800 border-blue-200',
      VIEWER: 'badge bg-gray-100 text-gray-800 border-gray-200'
    };
    return classes[role] || 'badge-info';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      ACTIVE: 'badge-success',
      INACTIVE: 'badge-secondary',
      LOCKED: 'badge-danger',
      PENDING: 'badge-warning'
    };
    return classes[status] || 'badge-info';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-4 h-4 text-success-600" />;
      case 'INACTIVE':
        return <Ban className="w-4 h-4 text-gray-600" />;
      case 'LOCKED':
        return <Lock className="w-4 h-4 text-danger-600" />;
      case 'PENDING':
        return <AlertTriangle className="w-4 h-4 text-warning-600" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage user accounts and permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadUsers}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <Link
            to="/users/create"
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create User</span>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Username, email, or name..."
                  className="input pl-10"
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="LOCKED">Locked</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                className="select"
                value={filters.role}
                onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="">All Roles</option>
                <option value="ADMIN">Admin</option>
                <option value="OPERATOR">Operator</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Users ({users.length})
          </h2>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading users...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">Error Loading Users</p>
                <p className="text-gray-600">{error}</p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No users found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 font-semibold text-sm">
                              {user.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                            <div className="text-sm text-gray-500">@{user.username}</div>
                            <div className="text-xs text-gray-400">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`${getRoleBadgeClass(user.role)} flex items-center space-x-1 w-fit`}>
                          <Shield className="w-3 h-3" />
                          <span>{user.role}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`${getStatusBadgeClass(user.status)} flex items-center space-x-1 w-fit`}>
                          {getStatusIcon(user.status)}
                          <span>{user.status}</span>
                        </span>
                        {user.mustChangePassword && (
                          <div className="mt-1">
                            <span className="badge bg-orange-100 text-orange-800 text-xs">
                              Password Reset Required
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.lastLoginAt ? (
                          <div>
                            <div className="text-sm text-gray-900">
                              {formatRelativeTime(user.lastLoginAt)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.lastLoginIp}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Never</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(user.createdAt, 'PP')}
                        </div>
                        {user.createdBy && (
                          <div className="text-xs text-gray-500">by {user.createdBy}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/users/${user.id}`}
                            className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link
                            to={`/users/${user.id}/edit`}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          {user.status === 'LOCKED' && (
                            <button
                              onClick={() => handleUnlock(user.id)}
                              className="text-success-600 hover:text-success-900 p-1 rounded hover:bg-success-50"
                              title="Unlock Account"
                            >
                              <Unlock className="w-4 h-4" />
                            </button>
                          )}
                          {user.status === 'ACTIVE' && (
                            <button
                              onClick={() => handleDeactivate(user.id)}
                              className="text-warning-600 hover:text-warning-900 p-1 rounded hover:bg-warning-50"
                              title="Deactivate User"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          )}
                          {user.status === 'INACTIVE' && (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="text-success-600 hover:text-success-900 p-1 rounded hover:bg-success-50"
                              title="Activate User"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleResetPassword(user.id, user.username)}
                            className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                            title="Reset Password"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
